import { useCallback, useRef, useState } from 'react';
import apiClient from '../apiClient/apiClient';
import {
  ExclusionSearchRequest,
  ExclusionSearchResponse,
  ExclusionTarget,
  isTargetComplete,
  missingTargetIds,
} from './exclusions.types';

/**
 * How many store/patch pairs go into one search request from the browser.
 *
 * The endpoint batches server-side and has no store cap, so this exists purely so the UI can report
 * progress: a whole-estate search is otherwise a single request that appears hung for its full duration.
 * Each call still fans out concurrently inside the backend, so this is about feedback, not throughput.
 */
export const SEARCH_CHUNK_SIZE = 500;

/** Progress of a running search, for the UI to render rather than a bare spinner. */
export interface ExclusionSearchProgress {
  storesSearched: number;
  storesTotal: number;
  chunksDone: number;
  chunksTotal: number;
}

export interface ExclusionSearchState {
  isSearching: boolean;
  progress: ExclusionSearchProgress | null;
  data: ExclusionSearchResponse | null;
  error: string | null;
}

const EMPTY_RESPONSE: ExclusionSearchResponse = { results: [], unreadable: [], failed: [] };

const chunk = <T,>(items: T[], size: number): T[][] => {
  if (size <= 0) return [items];

  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

export const searchExclusions = async (request: ExclusionSearchRequest): Promise<ExclusionSearchResponse> => {
  const response = await apiClient.post<ExclusionSearchResponse>('/api/exclusions/search', request);

  // Defensive: the lists are always present on a success, but a partial response should degrade to empty
  // lists rather than crashing a table that maps over them.
  return {
    results: response.data?.results ?? [],
    unreadable: response.data?.unreadable ?? [],
    failed: response.data?.failed ?? [],
  };
};

/**
 * Runs an exclusion search across a store set, reporting progress as it goes.
 *
 * Not react-query: the call is a POST driven by an explicit user action, the store set is large enough
 * that a cache key built from it is worthless, and the UI needs incremental progress rather than a single
 * settled result. Chunks are sequential on purpose - the backend already parallelises within a chunk, and
 * stacking browser concurrency on top of that only makes progress reporting dishonest.
 */
export const useExclusionSearch = () => {
  const [state, setState] = useState<ExclusionSearchState>({
    isSearching: false,
    progress: null,
    data: null,
    error: null,
  });

  // Lets a superseded or abandoned search stop writing to state.
  const runIdRef = useRef(0);

  const reset = useCallback(() => {
    runIdRef.current += 1;
    setState({ isSearching: false, progress: null, data: null, error: null });
  }, []);

  const search = useCallback(
    async (storeNumbers: string[], target: ExclusionTarget | null): Promise<ExclusionSearchResponse | null> => {
      const stores = storeNumbers.map((storeNumber) => storeNumber.trim()).filter(Boolean);

      if (!stores.length) {
        setState({ isSearching: false, progress: null, data: null, error: 'Select at least one store' });
        return null;
      }
      if (!isTargetComplete(target)) {
        const missing = target ? missingTargetIds(target).join(', ') : '';
        setState({
          isSearching: false,
          progress: null,
          data: null,
          error: missing ? `Target is incomplete: ${missing} required` : 'Choose what to exclude',
        });
        return null;
      }

      const chunks = chunk(stores, SEARCH_CHUNK_SIZE);
      const runId = runIdRef.current + 1;
      runIdRef.current = runId;

      setState({
        isSearching: true,
        error: null,
        data: null,
        progress: { storesSearched: 0, storesTotal: stores.length, chunksDone: 0, chunksTotal: chunks.length },
      });

      const merged: ExclusionSearchResponse = { results: [], unreadable: [], failed: [] };
      let searched = 0;

      for (let index = 0; index < chunks.length; index += 1) {
        const batch = chunks[index];

        try {
          const response = await searchExclusions({ storeNumbers: batch, target });
          merged.results.push(...response.results);
          merged.unreadable.push(...response.unreadable);
          merged.failed.push(...response.failed);
        } catch (error) {
          // One chunk failing must not discard the rest: its stores are recorded as failed, exactly as
          // the backend does for its own batches, and the search carries on.
          merged.failed.push(...batch);
        }

        if (runIdRef.current !== runId) return null;

        searched += batch.length;
        // Snapshotted into a const before the updater closes over it: `searched` is mutated by later
        // iterations, so referencing it from inside the callback would be a loop-scoped closure over a
        // changing variable (no-loop-func) and could report a progress count from the wrong iteration.
        const progress: ExclusionSearchProgress = {
          storesSearched: searched,
          storesTotal: stores.length,
          chunksDone: index + 1,
          chunksTotal: chunks.length,
        };
        setState((previous) => ({ ...previous, progress }));
      }

      if (runIdRef.current !== runId) return null;

      // Every chunk failed - report it as an error rather than an empty result that reads as "nothing
      // is excluded anywhere".
      const everythingFailed = merged.failed.length === stores.length && stores.length > 0;

      setState({
        isSearching: false,
        progress: null,
        data: everythingFailed ? EMPTY_RESPONSE : merged,
        error: everythingFailed ? 'Search failed for every store. Check the connection and try again.' : null,
      });

      return everythingFailed ? null : merged;
    },
    []
  );

  return { ...state, search, reset };
};
